import {
  uiPreferencesQueryKey,
  useMutationDeleteUiPreference,
} from "@apis:ui_preferences/ui_preferences";
import { ConfirmationDialog } from "@components:ConfirmationDialog";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useToasterStoreActions } from "@stores:global";
import { useTranslation } from "react-i18next";

interface UiPreferenceDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  uiPreferenceId: string;
}

export const UiPreferenceDeleteModal = ({
  isOpen,
  onClose,
  uiPreferenceId,
}: UiPreferenceDeleteModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const mutationDeleteUiPreference = useMutationDeleteUiPreference({
    onSuccess: async () => {
      toastActions.setToaster({
        variant: "success",
        message: t("preferences.uiPreferences.deleted"),
        open: true,
      });
      await invalidateQueries.run([
        {
          queryKey: uiPreferencesQueryKey._def,
        },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: t("preferences.uiPreferences.deleteFailed"),
        variant: "error",
      });
    },
  });

  const handleDelete = async () => {
    await mutationDeleteUiPreference.mutateAsync({ id: uiPreferenceId });
  };

  const toastActions = useToasterStoreActions();

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={() => onClose()}
      title={t("preferences.uiPreferences.deleteTitle")}
      description={t("preferences.uiPreferences.deleteConfirm")}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
            backgroundColor: (theme) => theme.palette.error.main,
          },
          label: t("common.delete"),
          onClick: async () => {
            await handleDelete();
          },
          disabled: !uiPreferenceId,
        },
      }}
    />
  );
};
